import React, { useState } from 'react';
import styled from 'styled-components';
import { useI18n } from '../contexts/I18nContext';

const LanguageSelectorContainer = styled.div`
  position: relative;
  margin-left: var(--space-4);
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  background: var(--primary-50);
  border: 1px solid var(--primary-200);
  border-radius: var(--radius-full);
  color: var(--primary-700);
  font-weight: 500;
  font-size: var(--font-size-xs);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: 'Inter', sans-serif;

  &:hover {
    background: var(--primary-100);
    border-color: var(--primary-300);
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--accent-200);
  }
`;

const LanguageFlag = styled.span`
  font-size: var(--font-size-lg);
  line-height: 1;
`;

const LanguageName = styled.span`
  font-weight: 500;

  @media (max-width: 768px) {
    display: none;
  }
`;

const LanguageDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + var(--space-2));
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--primary-200);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  min-width: 200px;
  max-height: ${props => props.isOpen ? '300px' : '0'};
  overflow: hidden;
  opacity: ${props => props.isOpen ? '1' : '0'};
  transform: translateY(${props => props.isOpen ? '0' : '-10px'});
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
`;

const LanguageOption = styled.button<{ isActive?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background: transparent;
  border: none;
  color: var(--primary-700);
  text-align: left;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: var(--font-size-sm);
  font-family: 'Inter', sans-serif;
  font-weight: 500;

  &:hover {
    background: var(--primary-50);
    color: var(--primary-900);
  }

  &:active {
    background: var(--primary-100);
    transform: scale(0.98);
  }

  ${props => props.isActive && `
    background: var(--warm-50);
    color: var(--warm-700);
    font-weight: 600;

    &:hover {
      background: var(--warm-100);
    }
  `}
`;

const LanguageSelector: React.FC = () => {
  const { language, setLanguage, availableLanguages } = useI18n();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = availableLanguages.find(lang => lang.code === language);

  const handleLanguageChange = (langCode: string) => {
    setLanguage(langCode as any);
    setIsOpen(false);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.language-selector')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <LanguageSelectorContainer className="language-selector">
      <LanguageButton onClick={() => setIsOpen(!isOpen)}>
        <LanguageFlag>{currentLanguage?.flag}</LanguageFlag>
        <LanguageName>{currentLanguage?.name}</LanguageName>
        <span>▼</span>
      </LanguageButton>

      <LanguageDropdown isOpen={isOpen}>
        {availableLanguages.map((lang) => (
          <LanguageOption
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            isActive={lang.code === language}
          >
            <LanguageFlag>{lang.flag}</LanguageFlag>
            <span>{lang.name}</span>
          </LanguageOption>
        ))}
      </LanguageDropdown>
    </LanguageSelectorContainer>
  );
};

export default LanguageSelector;